import { APP_BASE_HREF, DOCUMENT, Location } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';

import {
  DataAccessEventsFacade,
  DataAccessExhibitorsFacade,
  DataAccessExhibitorsPageFacade,
  DataAccessFairsFacade,
  DataAccessPostsFacade,
  DataAccessProductsFacade,
} from '@planone/bau365/data-access-fair-data';
import { DataAccessScreensizeFacade, SliderConfig } from '@planone/bau365/data-access-screensize';
import { BreadcrumbHelper } from '@planone/bau365/feature-breadcrumb';
import {
  LinkClickFacade,
  ShareButtonClickEventType,
  ShareDialogOpenEventType,
  ShareFacade,
  TabChangeFacade,
} from '@planone/bau365/feature-tracking';
import { Event, Exhibitor, Post, Product } from '@planone/bau365/util-fair-data';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { SWIPER_INTERACTION_EMITTER, SwiperFacade, SwiperInteractionEvent } from '@planone/shared/data-access-swiper';
import { BreadcrumbElement, ShareButtonsHelper } from '@planone/shared/ui-elements';
import { stringifyFilter } from '@planone/shared/util-interfaces';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { ImageFileTypes, ImageProviders } from '@planone/shared/util-pipelines';
import { BreadcrumbData, BreadcrumbGenerator } from '@planone/shared/util-structured-data';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  filter,
  Observable,
  Subject,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

@Component({
  selector: 'bau365-page-exhibitor-details',
  templateUrl: './page-exhibitor-details.component.html',
  styleUrls: ['./page-exhibitor-details.component.scss'],
})
export class PageExhibitorDetailsComponent implements OnInit, AfterViewChecked, OnDestroy {
  exhibitor$: Observable<Exhibitor | undefined>;
  coExhibitors$: Observable<Exhibitor[] | undefined>;
  parentExhibitor$: Observable<Exhibitor | undefined>;
  exhibitorLoading$: Observable<boolean | undefined>;
  exhibitorError$: Observable<string | undefined>;
  exhibitorProducts$: Observable<Product[] | undefined>;
  exhibitorProductsLoading$: Observable<boolean | undefined>;
  exhibitorProductsError$: Observable<string | undefined>;
  exhibitorPosts$: Observable<Post[] | undefined>;
  exhibitorPostsLoading$: Observable<boolean | undefined>;
  exhibitorPostsError$: Observable<string | undefined>;
  exhibitorEvents$: Observable<Event[] | undefined>;
  exhibitorEventsLoading$: Observable<boolean | undefined>;
  exhibitorEventsError$: Observable<string | undefined>;
  size$: Observable<string | undefined>;
  cardSliderConfig$: Observable<SliderConfig | null | undefined>;
  fairId$: Observable<string>;
  isBauInsights$: Observable<boolean>;
  imageZoomed = false;
  productCount = 0;
  tabIndex = 0;
  scrollBrands = false;
  brandsSectionScrolled = false;
  childrenLoading = false;
  socialShareUrl: string | undefined;
  size: string | undefined;
  breadcrumbElements: BreadcrumbElement[] | undefined;
  exhibitor?: Exhibitor = undefined;
  exhibitorId: string | undefined;
  exhibitorName: string | undefined;
  emailSubject = localizedMeta['shareEmailSubject'];
  emailBody = localizedMeta['shareEmailBody'];
  tabNameExhibitor = $localize`:@@featureExhibitorDetails-tabName-exhibitor:Aussteller`;
  tabNameCompany = $localize`:@@featureExhibitorDetails-tabName-company:Unternehmen`;
  tabNameExhibitorContent = $localize`:@@featureExhibitorDetails-tabName-exhibitorContent:Messeauftritt`;
  tabNameCompanyContent = $localize`:@@featureExhibitorDetails-tabName-companyContent:Unternehmensinhalte`;

  ImageFileTypes = ImageFileTypes;

  stringifyFilter = stringifyFilter;

  private unsubscribe: Subject<void> = new Subject<void>();

  @ViewChild('tabs') tabs?: unknown;
  brandsSection?: ElementRef;
  @ViewChild('shareButtonsTitle') public shareButtonsTitle: TemplateRef<never> | null | undefined;
  @ViewChild('shareButtons') public shareButtons: TemplateRef<never> | null | undefined;

  constructor(
    ProductsFacade: DataAccessProductsFacade,
    PostsFacade: DataAccessPostsFacade,
    EventsFacade: DataAccessEventsFacade,
    ScreensizeFacade: DataAccessScreensizeFacade,
    private ExhibitorsFacade: DataAccessExhibitorsFacade,
    private ExhibitorsPageFacade: DataAccessExhibitorsPageFacade,
    private FairsFacade: DataAccessFairsFacade,
    private trackingTabChangeFacade: TabChangeFacade,
    private trackingLinkClickFacade: LinkClickFacade,
    private trackingShareFacade: ShareFacade,
    private SwiperFacade: SwiperFacade,
    private activatedRoute: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private metaService: MetaService,
    private shareButtonsHelper: ShareButtonsHelper,
    private renderer2: Renderer2,
    private router: Router,
    private structuredDataGenerator: BreadcrumbGenerator,
    private location: Location,
    private breadcrumbHelper: BreadcrumbHelper,
    public togglesFacade: DataAccessTogglesFacade,
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Inject(SWIPER_INTERACTION_EMITTER) private swiperInteractionEmitter: BehaviorSubject<SwiperInteractionEvent>
  ) {
    this.metaService.setTitle(localizedMeta['titleExhibitorDetails'], [$localize`:@@pageMeta-titles-loading: Lädt...`]);

    this.exhibitor$ = ExhibitorsFacade.exhibitor$;
    this.parentExhibitor$ = ExhibitorsFacade.parentExhibitor$;
    this.coExhibitors$ = ExhibitorsFacade.coExhibitors$;
    this.exhibitorLoading$ = ExhibitorsFacade.exhibitorLoading$;
    this.exhibitorError$ = ExhibitorsFacade.exhibitorError$;
    this.exhibitorProducts$ = ProductsFacade.exhibitorProducts$;
    this.exhibitorProductsLoading$ = ExhibitorsFacade.exhibitorProductsLoading$;
    this.exhibitorProductsError$ = ExhibitorsFacade.exhibitorProductsError$;
    this.exhibitorPosts$ = PostsFacade.exhibitorPosts$;
    this.exhibitorPostsLoading$ = ExhibitorsFacade.exhibitorPostsLoading$;
    this.exhibitorPostsError$ = ExhibitorsFacade.exhibitorPostsError$;
    this.exhibitorEvents$ = EventsFacade.exhibitorEvents$;
    this.exhibitorEventsLoading$ = ExhibitorsFacade.exhibitorEventsLoading$;
    this.exhibitorEventsError$ = ExhibitorsFacade.exhibitorEventsError$;
    this.fairId$ = FairsFacade.fairId$;
    this.isBauInsights$ = FairsFacade.isBauInsights$;
    this.size$ = ScreensizeFacade.size$;
    this.cardSliderConfig$ = ScreensizeFacade.cardSliderConfig$;
  }

  ngOnInit() {
    this.swiperInteractionEmitter.pipe(takeUntil(this.unsubscribe)).subscribe((event: SwiperInteractionEvent) => {
      this.swiperInteraction(event);
    });

    this.activatedRoute.params.pipe(takeUntil(this.unsubscribe)).subscribe((params) => {
      this.ExhibitorsPageFacade.pageView(params['id'], params['fair_id']);
      this.ExhibitorsFacade.pageOpened(params['id'], params['fair_id']);
    });

    this.isBauInsights$
      .pipe(combineLatestWith(this.FairsFacade.fairConfig$), takeUntil(this.unsubscribe))
      .subscribe(([isBauInsights, fair]) => {
        this.metaService.removeOpenGraphMetaTags();

        if (isBauInsights) {
          this.metaService.addOpenGraphMetaTags(
            localizedMeta['linkPreviewTitleCompanyPage'],
            localizedMeta['linkPreviewDescriptionCompanyPage'],
            this.document.URL,
            this.baseHref + 'assets/link-preview-img.png',
            [fair.name]
          );
        } else {
          this.metaService.addOpenGraphMetaTags(
            localizedMeta['linkPreviewTitleExhibitorPage'],
            localizedMeta['linkPreviewDescriptionExhibitorPage'],
            this.document.URL,
            this.baseHref + 'assets/link-preview-img.png',
            [fair.name]
          );
        }
      });

    this.isBauInsights$
      .pipe(
        takeUntil(this.unsubscribe),
        filter((isBauInsights) => isBauInsights)
      )
      .subscribe(() => {
        this.metaService.setTitle(localizedMeta['titleCompanyDetails'], [
          $localize`:@@pageMeta-titles-loading: Lädt...`,
        ]);
      });

    this.exhibitor$
      .pipe(withLatestFrom(this.FairsFacade.fairConfig$), takeUntil(this.unsubscribe))
      .subscribe(([exhibitor, fair]) => {
        if (exhibitor) {
          this.exhibitorId = exhibitor.id;
          this.exhibitorName = exhibitor.name;
          this.exhibitor = exhibitor;
          this.childrenLoading = false;

          this.emailBody += $localize`:@@pageExhibitor-emailShare-entityType-exhibitor:Aussteller ${this.exhibitorName}`;

          this.socialShareUrl = this.shareButtonsHelper.buildShareUrlFromPath(
            'fairs/' + exhibitor.fair_id + '/exhibitors/' + exhibitor.id
          );

          const breadcrumbData: BreadcrumbData = this.breadcrumbHelper.prepareForExhibitor(fair, exhibitor);

          this.breadcrumbElements = breadcrumbData.elements;

          this.structuredDataGenerator.validateBreadcrumbStructuredData(
            this.renderer2,
            breadcrumbData.structuredData,
            this.document.head
          );
        }

        if (exhibitor?.products) {
          this.productCount = Object.keys(exhibitor?.products.items).length;
          this.changeDetectorRef.detectChanges();
        }
      });

    combineLatest([this.activatedRoute.params, this.activatedRoute.queryParams])
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(([params, queryParams]) => {
        if (!queryParams['tab'] || queryParams['tab'] === 'exhibitor') {
          this.tabIndex = 0;

          if (queryParams['brand']) {
            this.scrollBrands = true;
          }
        } else if (queryParams['tab'] === 'fair') {
          this.tabIndex = 1;

          if (!this.childrenLoading) {
            this.childrenLoading = true;

            this.ExhibitorsFacade.fairTabOpened(params['id'], params['fair_id']);
          }
        }
      });

    this.size$.pipe(takeUntil(this.unsubscribe)).subscribe((size) => (this.size = size));
  }

  ngAfterViewChecked() {
    if (!this.scrollBrands || !this.brandsSection?.nativeElement || this.brandsSectionScrolled) {
      return;
    }

    this.brandsSectionScrolled = true;

    setTimeout(() =>
      this.brandsSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' })
    );
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();

    this.metaService.removeOpenGraphMetaTags();
    this.structuredDataGenerator.removeTag(this.renderer2, this.document.head);
  }

  tabChange(event: MatTabChangeEvent) {
    let url = this.router.url;

    let queryParams = 'tab=fair';
    url = url.replace('?tab=fair', '');

    if (event.index !== 1) {
      queryParams = 'tab=exhibitor';
      url = url.replace('?tab=exhibitor', '');
    }

    this.location.replaceState(url, queryParams);

    this.trackTabChange(event);

    this.exhibitor$
      .pipe(take(1))
      .subscribe((next?: Exhibitor) => {
        if (event.index === 1 && next?.id && !this.childrenLoading) {
          this.childrenLoading = true;
          this.ExhibitorsFacade.fairTabOpened(next?.id, next?.fair_id ?? 'unknown');
        }
      })
      .unsubscribe();
  }

  trackTabChange(event: MatTabChangeEvent) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      this.ExhibitorsPageFacade.tabClicked({
        tabLabel: event.tab.ariaLabel,
        pageType: 'exhibitor-details',
        context: this.exhibitor?.fair_id ?? fairId,
        exhibitor: this.exhibitor,
      });

      this.trackingTabChangeFacade.trigger({
        context: this.exhibitor?.fair_id ?? fairId,
        pageType: 'exhibitor-details',
        tabLabel: event.tab.ariaLabel,
        entityType: 'EXHIBITOR',
      });
    });
  }

  trackShowAll(entityType: string) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.exhibitor) {
        this.ExhibitorsPageFacade.trackClickShowAllToJumpToSearch(this.exhibitor, entityType, fairId);
      }
    });
  }

  trackDownload(element: EventTarget | null, fileName: string) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.exhibitor) {
        this.ExhibitorsPageFacade.trackedItemClicked({
          htmlElement: element,
          linkText: fileName,
          linkType: 'DOWNLOAD',
          exhibitor: this.exhibitor,
          context: fairId,
          pageType: 'exhibitor-details',
        });

        this.trackingLinkClickFacade.trigger({
          entityType: 'EXHIBITOR',
          linkType: 'DOWNLOAD',
          linkText: fileName,
          context: this.exhibitor.fair_id ?? fairId,
          pageType: 'exhibitor-details',
          eventTarget: element ?? undefined,
        });
      }
    });
  }

  trackClick(element: EventTarget | null, linkType: string, linkText?: string, contactPersonId?: string) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (linkText && this.exhibitor) {
        this.ExhibitorsPageFacade.trackedItemClicked({
          htmlElement: element,
          linkType: linkType,
          exhibitor: this.exhibitor,
          context: fairId,
          pageType: 'exhibitor-details',
          ...(linkText && { linkText: linkText }),
          ...(contactPersonId && { contactPersonId: contactPersonId }),
        });

        this.trackingLinkClickFacade.trigger({
          entityType: 'EXHIBITOR',
          linkType: linkType,
          linkText: linkText,
          context: this.exhibitor.fair_id ?? fairId,
          pageType: 'exhibitor-details',
          eventTarget: element ?? undefined,
          ...(contactPersonId && { contactPerson: { id: contactPersonId } }),
        });
      }
    });
  }

  trackShareDialogOpened(event: ShareDialogOpenEventType) {
    this.trackingShareFacade.open(event);
  }

  trackShareDialogClicked(event: ShareButtonClickEventType) {
    this.trackingShareFacade.click(event);
  }

  swiperInteraction(event: SwiperInteractionEvent) {
    const slide = (this.exhibitor?.gallery ?? [])[event.index];
    const count = (this.exhibitor?.gallery ?? []).length;

    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (slide) {
        this.SwiperFacade.interaction({
          interaction_type: event.type,
          image_resource: slide.resource,
          image_provider: <ImageProviders>slide.provider,
          image_description: slide.description,
          image_position: event.index + 1,
          context: fairId,
          page_type: 'exhibitor-details',
          exhibitor: this.exhibitor,
          image_count: count,
        });
      }
    });
  }
}
